import React, { forwardRef, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import Paragraph from '@polestar/component-warehouse-react/components/paragraph/Paragraph';
import Row from '@polestar/component-warehouse-react/components/row/Row';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { CrossAxisAlignments } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Tabs from '@polestar/component-warehouse-react/components/tabs/Tabs';
import { createPaginationTabs } from './Pagination.helpers';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import TextField from '@polestar/component-warehouse-react/components/text-field/TextField';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
/**
 * @internal Only used for A/B testing currently
 */
export var PaginationVariants;
(function (PaginationVariants) {
    PaginationVariants["auto"] = "auto";
    PaginationVariants["experimentalMinimal"] = "experimentalMinimal";
})(PaginationVariants || (PaginationVariants = {}));
const Pagination = forwardRef(({ id, activeIndex = 1, variant = PaginationVariants.auto, maxIndex = 1, onChange, indexAriaLabel, previousButtonProps, nextButtonProps, ...props }, ref) => {
    const memoizedId = useSSRSafeId('pagination', id);
    const isMobile = useMediaQuery().queryMatches.mobile;
    const tabs = useMemo(() => createPaginationTabs(activeIndex, maxIndex, indexAriaLabel), [activeIndex, indexAriaLabel, maxIndex]);
    const handleLeft = useCallback(() => {
        onChange === null || onChange === void 0 ? void 0 : onChange(activeIndex - 1);
    }, [activeIndex, onChange]);
    const handleRight = useCallback(() => {
        onChange === null || onChange === void 0 ? void 0 : onChange(activeIndex + 1);
    }, [activeIndex, onChange]);
    const handleTabClick = useCallback((index) => {
        const value = Number(tabs.list[index].label);
        if (value !== activeIndex) {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }
    }, [activeIndex, onChange, tabs.list]);
    const handleTextChange = useCallback((value) => {
        const val = !isNaN(Number(value)) ? Number(value) : undefined;
        if (val) {
            onChange === null || onChange === void 0 ? void 0 : onChange(val);
        }
    }, [onChange]);
    if (activeIndex > maxIndex) {
        console.warn('Pagination: the current "activeIndex" is higher that the set "maxIndex" which is invalid, please adjust the maximum value higher than "activeIndex".');
        return null;
    }
    if (maxIndex < 1) {
        console.warn('Pagination: "maxIndex" should be at least 1.');
        return null;
    }
    if (activeIndex < 1) {
        console.warn('Pagination: "activeIndex" should be at least 1.');
        return null;
    }
    const options = {
        [PaginationVariants.auto]: isMobile ? (React.createElement(StyledContentRowMobile, { crossAxisAlignment: CrossAxisAlignments.center },
            React.createElement(Paragraph, { "aria-current": "page" }, activeIndex),
            React.createElement(Spacer, { spacing: Spacings.xxSmall, orientation: Orientations.horizontal }),
            React.createElement(Paragraph, null,
                "/ ",
                maxIndex))) : (React.createElement(StyledContentRow, { crossAxisAlignment: CrossAxisAlignments.center },
            React.createElement(StyledTabs, { id: `${memoizedId}-tabs`, activeTab: tabs.currentIndex, onTabClick: handleTabClick, tabs: tabs.list, minLabelWidth: maxIndex.toString().length > 2
                    ? `${0.5625 * maxIndex.toString().length}em`
                    : //  Tab padding + Spacings.large = 48px which is the minimum hitbox width
                        Spacings.large, key: maxIndex, role: "navigation" }))),
        [PaginationVariants.experimentalMinimal]: (React.createElement(StyledContentRowMobile, { crossAxisAlignment: CrossAxisAlignments.center },
            React.createElement(StyledTextField, { value: activeIndex, onChange: handleTextChange }),
            React.createElement(Paragraph, null,
                " / ",
                maxIndex))),
    };
    const content = options[variant];
    return (React.createElement(StyledRow, { id: memoizedId, ...props, ref: ref },
        React.createElement(PaginationButton, { ...previousButtonProps, icon: Icons.arrowLeft, pairedWithText: true, onClick: handleLeft, disabled: activeIndex === 1 }),
        React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: createCustomSpacing(6 / 16) }),
        content,
        React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: createCustomSpacing(6 / 16) }),
        React.createElement(PaginationButton, { ...nextButtonProps, icon: Icons.arrowRight, pairedWithText: true, onClick: handleRight, disabled: activeIndex === maxIndex })));
});
Pagination.displayName = 'Pagination';
export default Pagination;
const PaginationButton = ({ icon, onClick, disabled, ...props }) => (React.createElement(IconButton, { ...aria({
        disabled,
    }), disabled: disabled, onClick: onClick, icon: icon, variant: IconButtonVariants.transparent, ...props }));
const StyledRow = styled(Row) `
  min-height: ${createCustomSpacing(40 / 16)};

  ${responsiveStyles({
    alignItems: {
        mobile: 'center',
    },
})}
`;
const StyledTextField = styled(TextField) `
  max-width: 2rem;
  text-align: center;
`;
const StyledContentRow = styled(Row) `
  padding: 0 ${Spacings.medium};
  margin-block-start: ${createCustomSpacing(1 / 16)};

  ul {
    /* This overwrites the 0px hack in Tabs */
    width: auto;
  }
`;
const StyledTabs = styled(Tabs) `
  ul {
    gap: ${createCustomSpacing(12 / 16)};

    li {
      justify-content: center;
      align-items: center;

      button {
        min-width: ${createCustomSpacing(36 / 16)};
      }
    }
  }
`;
const StyledContentRowMobile = styled(StyledContentRow) `
  padding: 0 ${Spacings.xLarge};
  min-width: ${Spacings.xxxxxxLarge};
  align-items: center;
  justify-content: center;
`;
